/**
 * Status, filter, and display config for roles.
 */
import { CheckCircle2, XCircle, Users, UserCog, Monitor } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { BadgeVariant } from '@/components/common/LabelBadge';
import { SUPPORT_TYPE_LABEL } from '@/constants/shared-catalog';

export { SUPPORT_TYPE_LABEL };
import { getRoleLabel, getRoleLabelPlural } from '@/utils/role-label-utils';

/** Badge + icon config for a role operating mode. */
export interface RoleModeConfig {
  label: string;
  icon: LucideIcon;
  badgeVariant: BadgeVariant;
  badgeColor: string;
}

/** Badge + icon config for a role status. */
export interface RoleStatusConfig {
  label: string;
  icon: LucideIcon;
  badgeVariant: BadgeVariant;
  badgeColor?: string;
}

/** Role list filter state. */
export interface RoleFilters {
  roleGroups: string[];
  statuses: ('active' | 'inactive')[];
  modes: ('in_person' | 'remote' | 'hybrid')[];
  searchTerm?: string;
  minHierarchyLevel?: number;
  maxHierarchyLevel?: number;
}

/** Filter group IDs for the roles {@link DataTable} configure panel. */
export type FilterGroupId =
  | 'filter-role-groups'
  | 'filter-status'
  | 'filter-modes'
  | 'filter-hierarchy'
  | 'filter-search';

/** Badge config for each operating mode. */
export const ROLE_MODE_CONFIG: Record<string, RoleModeConfig> = {
  in_person: {
    label: SUPPORT_TYPE_LABEL.in_person,
    icon: Users,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-2-nuance-1)',
  },
  remote: {
    label: SUPPORT_TYPE_LABEL.remote,
    icon: Monitor,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-3-nuance-1)',
  },
  hybrid: {
    label: SUPPORT_TYPE_LABEL.hybrid,
    icon: UserCog,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-1-nuance-1)',
  },
};

/** Badge config for each role status. */
export const ROLE_STATUS_CONFIG: Record<string, RoleStatusConfig> = {
  active: {
    label: 'Actif',
    icon: CheckCircle2,
    badgeVariant: 'active',
  },
  inactive: {
    label: 'Inactif',
    icon: XCircle,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--color-error))',
  },
};

/** Default role list filters. */
export const DEFAULT_ROLE_FILTERS: RoleFilters = {
  roleGroups: [],
  statuses: [],
  modes: [],
  searchTerm: undefined,
  minHierarchyLevel: undefined,
  maxHierarchyLevel: undefined,
};

/** Configure-panel order for role filter groups. */
export const ALL_FILTER_GROUP_IDS: FilterGroupId[] = [
  'filter-role-groups',
  'filter-status',
  'filter-modes',
  'filter-hierarchy',
  'filter-search',
];

/** Human-readable label per filter group (configure panel). */
export const FILTER_GROUP_LABELS: Record<FilterGroupId, string> = {
  'filter-role-groups': 'Familles de rôles',
  'filter-status': 'Statut',
  'filter-modes': 'Modes opérationnels',
  'filter-hierarchy': 'Niveaux hiérarchiques',
  'filter-search': 'Recherche',
};

/** Interfaces always granted by the backend — excluded from the builder and saved bindings. */
export const UNIVERSAL_INTERFACES = new Set(['dashboard', 'sidebar']);

/** Scope relation codes available in the role builder. */
export const AVAILABLE_SCOPE_RELATIONS = [
  'establishment',
  'program',
  'department',
  'managedBy',
  'assignment',
  'subject',
] as const;

/** Label and description for a data-access scope. */
export interface ScopeLabel {
  code: string;
  label: string;
  description: string;
}

/** Label + description for each data-access scope. */
export const SCOPE_LABELS: Record<string, ScopeLabel> = {
  unrestricted: {
    code: 'unrestricted',
    label: 'Toutes les données',
    description: "Voit toutes les données de l'organisation",
  },
  establishment: {
    code: 'establishment',
    label: 'Ses établissements',
    description: 'Seulement les données liées à ses établissements',
  },
  program: {
    code: 'program',
    label: 'Son programme',
    description: 'Seulement les données de son programme',
  },
  department: {
    code: 'department',
    label: 'Son département',
    description: 'Seulement les données du même département',
  },
  managedBy: {
    code: 'managedBy',
    label: "Personnes qu'il supervise",
    description: 'Seulement les personnes dont il est responsable',
  },
  assignment: {
    code: 'assignment',
    label: 'Ses propres données',
    description: 'Seulement les données auxquelles il est directement assigné',
  },
  subject: {
    code: 'subject',
    label: 'Sa matière',
    description: 'Seulement les données liées à sa spécialité',
  },
  roleTarget: {
    code: 'roleTarget',
    label: 'Utilisateurs de rôle ciblé',
    description:
      'Utilisateurs ayant un rôle spécifique (nécessite de préciser le code du rôle cible)',
  },
};

/** {@link Select} option for an entity field value. */
export interface EntityFieldOption {
  value: string;
  label: string;
}

/** Field + options used to scope a module entity. */
export interface ModuleEntityFields {
  field: string;
  label: string;
  options: EntityFieldOption[];
}

/** Entity fields available per module in the role builder. */
export const MODULE_ENTITY_FIELDS: Record<string, ModuleEntityFields[]> = {
  sessions: [
    {
      field: 'type',
      label: 'Type de séance',
      options: [
        { value: 'homework_help', label: 'Aide aux devoirs' },
        { value: 'individual_tutoring', label: 'Tutorat individuel' },
        { value: 'workshop', label: 'Atelier' },
        { value: 'whole_class', label: 'Accompagnement en classe' },
        { value: 'group_tutoring', label: 'Tutorat de groupe' },
        { value: 'quick_help_desk', label: "Bureau d'aide rapide" },
      ],
    },
    {
      field: 'sessionFormat',
      label: 'Format de séance',
      options: [{ value: 'in_person', label: 'Présentiel' }],
    },
  ],
  users: [
    {
      field: 'roles',
      label: 'Rôles',
      options: [
        {
          value: 'student',
          get label() {
            return getRoleLabel('student');
          },
        },
        {
          value: 'tutor',
          get label() {
            return getRoleLabel('tutor');
          },
        },
        {
          value: 'volunteer',
          get label() {
            return getRoleLabel('volunteer');
          },
        },
        {
          value: 'coordinator',
          get label() {
            return getRoleLabel('coordinator');
          },
        },
        {
          value: 'manager',
          get label() {
            return getRoleLabel('manager');
          },
        },
        {
          value: 'admin',
          get label() {
            return getRoleLabel('admin');
          },
        },
      ],
    },
  ],
  admins: [
    {
      field: 'role',
      label: "Type d'utilisateur",
      options: [
        {
          value: 'admin',
          get label() {
            return getRoleLabel('admin');
          },
        },
        {
          value: 'coordinator',
          get label() {
            return getRoleLabel('coordinator');
          },
        },
        {
          value: 'manager',
          get label() {
            return getRoleLabel('manager');
          },
        },
      ],
    },
  ],
  volunteers: [
    {
      field: 'role',
      label: "Type d'utilisateur",
      options: [
        {
          value: 'volunteer',
          get label() {
            return getRoleLabel('volunteer');
          },
        },
        {
          value: 'professor',
          get label() {
            return getRoleLabel('professor');
          },
        },
      ],
    },
  ],
  tutors: [
    {
      field: 'role',
      label: "Type d'utilisateur",
      options: [
        {
          value: 'tutor',
          get label() {
            return getRoleLabel('tutor');
          },
        },
      ],
    },
  ],
  students: [
    {
      field: 'role',
      label: "Type d'utilisateur",
      options: [
        {
          value: 'student',
          get label() {
            return getRoleLabel('student');
          },
        },
      ],
    },
  ],
};

/** Display labels for each permission module. */
export const MODULE_DISPLAY_LABELS: Record<string, string> = {
  get admins() {
    return getRoleLabelPlural('admin');
  },
  users: 'Utilisateurs',
  dashboard: 'Tableau de bord',
  sidebar: 'Navigation',
  sessions: 'Séances',
  matching: 'Gestion des binômes',
  pairs: 'Binômes',
  get students() {
    return getRoleLabelPlural('student');
  },
  get tutors() {
    return getRoleLabelPlural('tutor');
  },
  get volunteers() {
    return getRoleLabelPlural('volunteer');
  },
  roles: 'Rôles et permissions',
  establishments: 'Établissements',
  reports: 'Comptes rendus',
  get vscReports() {
    return `Comptes rendus ${getRoleLabel('volunteer')}`;
  },
  activities: 'Activités',
  activityTypes: "Types d'activités",
  holidays: 'Jours fériés',
  programs: 'Programmes',
  subjects: 'Matières',
  levels: 'Niveaux',
  schoolYears: 'Années scolaires',
  specialties: 'Spécialités',
  'help-support': 'Aide et support',
};

/** Local-storage key for the post-its board toggle. */
export const POSTITS_BOARD_TOGGLE_KEY = 'postits-board-toggle';

/** Permission action category used to group role actions. */
export type ActionCategory =
  | 'consultation'
  | 'creation'
  | 'modification'
  | 'suppression'
  | 'export_import'
  | 'validation'
  | 'other';

/** Human-readable label per action category. */
export const ACTION_CATEGORY_LABELS: Record<ActionCategory, string> = {
  consultation: 'Consultation',
  creation: 'Création',
  modification: 'Modification',
  suppression: 'Suppression',
  export_import: 'Export / Import',
  validation: 'Validation',
  other: 'Autres',
};

/** Display order for action categories. */
export const ACTION_CATEGORY_ORDER: ActionCategory[] = [
  'consultation',
  'creation',
  'modification',
  'suppression',
  'export_import',
  'validation',
  'other',
];
